/**
 * Minimal protobuf (proto3) wire codec.
 *
 * Hand-written rather than generated: the browser client needs perhaps thirty
 * of RustDesk's messages, and this keeps the bundle small and dependency-free
 * while making the framing explicit. Unknown fields are skipped on read and
 * never emitted on write, which is exactly proto3's forward-compatibility rule.
 */
export class Writer {
    #chunks = [];
    get length() {
        return this.#chunks.length;
    }
    raw(bytes) {
        for (const b of bytes)
            this.#chunks.push(b);
        return this;
    }
    varint(value) {
        let v = typeof value === "bigint" ? value : BigInt(Math.floor(value));
        if (v < 0n)
            v += 1n << 64n; // two's complement, as protobuf encodes negatives
        do {
            let byte = Number(v & 0x7fn);
            v >>= 7n;
            if (v > 0n)
                byte |= 0x80;
            this.#chunks.push(byte);
        } while (v > 0n);
        return this;
    }
    tag(field, type) {
        return this.varint((field << 3) | type);
    }
    uint32(field, value) {
        if (!value)
            return this; // proto3: default values are not serialised
        return this.tag(field, 0 /* WireType.Varint */).varint(value);
    }
    int32(field, value) {
        if (!value)
            return this;
        return this.tag(field, 0 /* WireType.Varint */).varint(value);
    }
    /**
     * Writes the field even when the value is zero.
     *
     * proto3 leaves defaults off the wire, which is right for an ordinary field
     * and wrong for a member of a `oneof`: there, absence means "no variant
     * chosen at all", so a zero written the usual way turns a "stop doing that"
     * into a message the peer ignores.
     */
    int32Present(field, value) {
        return this.tag(field, 0 /* WireType.Varint */).varint(value);
    }
    boolPresent(field, value) {
        return this.tag(field, 0 /* WireType.Varint */).varint(value ? 1 : 0);
    }
    int64(field, value) {
        if (!value)
            return this;
        return this.tag(field, 0 /* WireType.Varint */).varint(value);
    }
    /** zigzag-encoded signed int, for `sint32` fields. */
    sint32(field, value) {
        if (!value)
            return this;
        const zig = (value << 1) ^ (value >> 31);
        return this.tag(field, 0 /* WireType.Varint */).varint(zig >>> 0);
    }
    bool(field, value) {
        if (!value)
            return this;
        return this.tag(field, 0 /* WireType.Varint */).varint(1);
    }
    string(field, value) {
        if (!value)
            return this;
        return this.bytes(field, new TextEncoder().encode(value));
    }
    bytes(field, value) {
        if (!value || value.length === 0)
            return this;
        this.tag(field, 2 /* WireType.Bytes */).varint(value.length);
        return this.raw(value);
    }
    /**
     * A packed repeated varint field.
     *
     * proto3 packs repeated scalar and enum fields by default, so `repeated
     * ControlKey modifiers` goes out as one length-delimited run of varints, not
     * as one tag per element. Emitting it unpacked still parses, but does not
     * match what the Rust peer produces.
     */
    packedVarints(field, values) {
        if (!values || values.length === 0)
            return this;
        const inner = new Writer();
        for (const v of values)
            inner.varint(v);
        const bytes = inner.finish();
        this.tag(field, 2 /* WireType.Bytes */).varint(bytes.length);
        return this.raw(bytes);
    }
    /** Nested message. Always emitted when present, even if empty. */
    message(field, value) {
        if (!value)
            return this;
        this.tag(field, 2 /* WireType.Bytes */).varint(value.length);
        return this.raw(value);
    }
    finish() {
        return new Uint8Array(this.#chunks);
    }
}
export class Reader {
    #buf;
    #pos = 0;
    constructor(buf) {
        this.#buf = buf;
    }
    get eof() {
        return this.#pos >= this.#buf.length;
    }
    get pos() {
        return this.#pos;
    }
    varint() {
        let result = 0n;
        let shift = 0n;
        for (;;) {
            if (this.#pos >= this.#buf.length)
                throw new Error("protobuf: truncated varint");
            const byte = this.#buf[this.#pos++];
            result |= BigInt(byte & 0x7f) << shift;
            if ((byte & 0x80) === 0)
                break;
            shift += 7n;
            if (shift > 63n)
                throw new Error("protobuf: varint overflow");
        }
        return result;
    }
    /** Reads a field header. Returns null at end of buffer. */
    tag() {
        if (this.eof)
            return null;
        const key = Number(this.varint());
        return { field: key >>> 3, type: (key & 7) };
    }
    uint32() {
        return Number(this.varint() & 0xffffffffn);
    }
    int32() {
        // Negative int32 is sign-extended to 64 bits on the wire.
        const v = this.varint();
        const masked = Number(v & 0xffffffffn);
        return masked | 0;
    }
    int64() {
        return this.varint();
    }
    sint32() {
        const v = Number(this.varint() & 0xffffffffn);
        return (v >>> 1) ^ -(v & 1);
    }
    bool() {
        return this.varint() !== 0n;
    }
    bytes() {
        const len = Number(this.varint());
        if (this.#pos + len > this.#buf.length)
            throw new Error("protobuf: truncated bytes");
        const out = this.#buf.subarray(this.#pos, this.#pos + len);
        this.#pos += len;
        return out;
    }
    string() {
        return new TextDecoder().decode(this.bytes());
    }
    /** Skips a field whose number we do not know. */
    skip(type) {
        switch (type) {
            case 0 /* WireType.Varint */:
                this.varint();
                return;
            case 1 /* WireType.Fixed64 */:
                this.#pos += 8;
                return;
            case 2 /* WireType.Bytes */:
                this.bytes();
                return;
            case 5 /* WireType.Fixed32 */:
                this.#pos += 4;
                return;
            default:
                throw new Error(`protobuf: unsupported wire type ${type}`);
        }
    }
}
/** Iterates fields, calling `fn` for each; unknown fields are skipped. */
export function eachField(buf, fn) {
    const r = new Reader(buf);
    for (;;) {
        const t = r.tag();
        if (!t)
            break;
        const handled = fn(t.field, t.type, r);
        if (handled !== true)
            r.skip(t.type);
    }
}
/**
 * RustDesk's TCP length prefix (see BytesCodec in hbb_common).
 *
 * The low 2 bits of the first byte give (headerLength - 1); the length itself
 * is the little-endian header value shifted right by 2.
 *
 * Note: WebSocket transports do NOT use this - each ws binary frame is exactly
 * one message. It is here for completeness and for tests against raw TCP.
 */
export function encodeFrameLength(n) {
    if (n < 0x40)
        return new Uint8Array([n << 2]);
    if (n < 0x4000) {
        const v = (n << 2) | 1;
        return new Uint8Array([v & 0xff, (v >> 8) & 0xff]);
    }
    if (n < 0x400000) {
        const v = (n << 2) | 2;
        return new Uint8Array([v & 0xff, (v >> 8) & 0xff, (v >> 16) & 0xff]);
    }
    const v = (n << 2) | 3;
    return new Uint8Array([v & 0xff, (v >> 8) & 0xff, (v >> 16) & 0xff, (v >> 24) & 0xff]);
}
export function decodeFrameLength(buf) {
    if (buf.length < 1)
        return null;
    const headerSize = (buf[0] & 0x3) + 1;
    if (buf.length < headerSize)
        return null;
    let n = buf[0];
    if (headerSize > 1)
        n |= buf[1] << 8;
    if (headerSize > 2)
        n |= buf[2] << 16;
    if (headerSize > 3)
        n |= buf[3] << 24;
    return { length: n >>> 2, headerSize };
}
